/**
 * Role default dashboards (Beta-1 plan P3-04 / P3-19) — one config for every dashboard the
 * framework runs. A default is an ORDERED list of `{ widgetId, size?, props? }`; the layout is
 * built by placing each in the first free slot (addItem), so widgets missing from the registry
 * (not migrated yet, flagged off, above the viewer's level) are simply skipped.
 *
 *   ROLE_DEFAULT_LAYOUTS[dashboardKey][roleKey | '*']
 *
 * Widget ids name the widgets P3-20 registers (today's dashboard sections). Until a widget
 * exists the default just has fewer cards — nothing breaks. Tim (C0813/C0820): plant manager
 * = my plant + alerts + what changed + OE Conditions + REPS cards; FLS = crew OE conditions
 * first; OE Conditions list at the bottom of every role's dashboard (D06.1).
 */
import { DASHBOARD_KEYS, isWidgetAllowed } from './widgetRegistry.js';
import { addItem, createLayout } from './layoutModel.js';

const ANY = '*';

export const ROLE_DEFAULT_LAYOUTS = Object.freeze({
  [DASHBOARD_KEYS.repsit]: {
    [ANY]: [
      { widgetId: 'drew-status', size: 'M' },
      { widgetId: 'getting-started', size: 'M' },
      { widgetId: 'access-drew', size: 'XL' },
      { widgetId: 'help-shortcuts', size: 'W' }, // full row — a half-width card beside an empty slot (UI review §2)
      { widgetId: 'notifications', size: 'M' }, // P7 — not registered yet
    ],
  },
  // OE Dashboard: alerts stay a fixed banner above the grid (Tim, M-0818), so no alert widget
  // here. The `*` default reproduces today's page order (P3-20 test).
  [DASHBOARD_KEYS.drewOe]: {
    [ANY]: [
      { widgetId: 'reps-quadrants', size: 'W' },
      { widgetId: 'oe-breakdown', size: 'W' },
      { widgetId: 'oe-trend', size: 'XL' },
      // Surveys compact (Tim C0820) and Case Studies off by default — D10 moved the Learning Library
      // to the REPSit sidebar; the widget stays in the gallery (⚑ D-UI-04, UI review 2026-09-21).
      { widgetId: 'active-surveys', size: 'M' },
      { widgetId: 'recent-activity', size: 'M' },
      { widgetId: 'oe-conditions', size: 'XL' },
      { widgetId: 'guidance', size: 'XL' },
    ],
    site_manager: [
      { widgetId: 'what-changed', size: 'L' },
      { widgetId: 'reps-quadrants', size: 'L' },
      { widgetId: 'oe-trend', size: 'L' },
      { widgetId: 'oe-breakdown', size: 'W' },
      // Active Surveys left out of the non-admin defaults (Tim C0820: "there will likely only ever be
      // one for anybody other than the administrator") — still in the gallery.
      { widgetId: 'recent-activity', size: 'M' },
      { widgetId: 'oe-conditions', size: 'XL' },
    ],
    team_lead: [
      { widgetId: 'oe-conditions', size: 'XL' },
      { widgetId: 'l1-queue', size: 'M' },
      { widgetId: 'reps-quadrants', size: 'L' },
      { widgetId: 'oe-trend', size: 'L' },
    ],
  },
  // Access-level dashboards (B4): every id below is registered by roleDashboardWidgets.jsx or
  // oeDashboardWidgets.jsx. Order = what the persona needs first (Tim C0813 / C0820).
  [DASHBOARD_KEYS.drewItm]: {
    [ANY]: [
      { widgetId: 'my-submissions', size: 'L' },
      { widgetId: 'my-nwt-scores', size: 'L' },
      { widgetId: 'my-site-scores', size: 'W' },
      { widgetId: 'active-surveys', size: 'M' },
      { widgetId: 'getting-started', size: 'M' },
      { widgetId: 'oe-conditions', size: 'XL' },
    ],
  },
  [DASHBOARD_KEYS.drewFls]: {
    [ANY]: [
      { widgetId: 'l1-queue', size: 'L' },
      { widgetId: 'my-nwt-scores', size: 'L' },
      { widgetId: 'oe-conditions', size: 'XL' },
      { widgetId: 'risk-alerts', size: 'M' },
      { widgetId: 'active-surveys', size: 'M' },
      { widgetId: 'what-changed', size: 'L' },
    ],
  },
  [DASHBOARD_KEYS.drewDh]: {
    [ANY]: [
      { widgetId: 'dept-scores', size: 'W' },
      { widgetId: 'l2-queue', size: 'L' },
      { widgetId: 'nwts-in-dept', size: 'L' },
      { widgetId: 'comparisons', size: 'L' },
      { widgetId: 'what-changed', size: 'L' },
      { widgetId: 'oe-conditions', size: 'XL' },
      { widgetId: 'oe-trend', size: 'L' },
      { widgetId: 'report-shortcuts', size: 'M' },
    ],
  },
  [DASHBOARD_KEYS.drewExec]: {
    [ANY]: [
      { widgetId: 'enterprise-scores', size: 'W' },
      { widgetId: 'risk-alerts', size: 'L' },
      { widgetId: 'what-changed', size: 'L' },
      { widgetId: 'comparisons', size: 'L' },
      { widgetId: 'sites-glance', size: 'L' },
      { widgetId: 'divergences', size: 'L' },
      { widgetId: 'oe-trend', size: 'L' },
      { widgetId: 'report-shortcuts', size: 'M' },
      { widgetId: 'oe-conditions', size: 'XL' },
    ],
  },
});

/** The default spec list for a dashboard + role (role entry, else the `*` entry, else []). */
export function defaultSpecs(dashboardKey, role, table = ROLE_DEFAULT_LAYOUTS) {
  const byRole = table[dashboardKey];
  if (!byRole) return [];
  return byRole[role] || byRole[ANY] || [];
}

/**
 * Build the default layout for a dashboard + role from the registry: each spec whose widget
 * exists and is allowed for the viewer is placed in order at the first free slot.
 * `ctx` is the availability context (role, dashboard, feature flags…).
 */
export function defaultLayoutFor(dashboardKey, role, registry, { table = ROLE_DEFAULT_LAYOUTS, ...ctx } = {}) {
  let layout = createLayout([]);
  for (const spec of defaultSpecs(dashboardKey, role, table)) {
    const widget = registry.get(spec.widgetId);
    if (!widget) continue;
    if (!isWidgetAllowed(widget, { role, dashboard: dashboardKey, ...ctx })) continue;
    layout = addItem(layout, widget, { size: spec.size, props: spec.props || {} }).layout;
  }
  return layout;
}
